import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

type VerifastPopupCloseReason = "manual" | "complete";

type VerifastMessageData = {
  event?: string;
  [key: string]: unknown;
};

interface VerifastIframePopupProps {
  isOpen: boolean;
  iframeUrl: string | null;
  verifastOrigin?: string;
  isLoading?: boolean;
  onClose: (reason: VerifastPopupCloseReason) => void;
  onStarted?: () => void;
  /** Fired when Verifast reports a step complete; popup stays open until user closes it. */
  onStepComplete?: () => void;
}

const STEP_COMPLETE_EVENTS = new Set([
  "complete",
  "identity-verification-complete",
]);

/** If postMessage never arrives (origin mismatch), still reveal after this delay. */
const IFRAME_REVEAL_FALLBACK_MS = 10000;

export function VerifastIframePopup({
  isOpen,
  iframeUrl,
  verifastOrigin,
  isLoading = false,
  onClose,
  onStarted,
  onStepComplete,
}: VerifastIframePopupProps) {
  const [errorMessage, setErrorMessage] = useState("");
  const closeHandledRef = useRef(false);
  useBodyScrollLock(isOpen);

  const normalizedOrigin = useMemo(
    () => (verifastOrigin || "").replace(/\/+$/, ""),
    [verifastOrigin],
  );

  const iframeSrc = useMemo(() => iframeUrl || "", [iframeUrl]);
  const [iframeRevealed, setIframeRevealed] = useState(false);
  const revealFallbackTimerRef = useRef<number | null>(null);

  const revealIframe = useCallback(() => {
    setIframeRevealed(true);
    if (revealFallbackTimerRef.current != null) {
      window.clearTimeout(revealFallbackTimerRef.current);
      revealFallbackTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !iframeSrc) {
      setIframeRevealed(false);
      if (revealFallbackTimerRef.current != null) {
        window.clearTimeout(revealFallbackTimerRef.current);
        revealFallbackTimerRef.current = null;
      }
      return;
    }

    setIframeRevealed(false);
    revealFallbackTimerRef.current = window.setTimeout(
      () => revealIframe(),
      IFRAME_REVEAL_FALLBACK_MS,
    );

    return () => {
      if (revealFallbackTimerRef.current != null) {
        window.clearTimeout(revealFallbackTimerRef.current);
        revealFallbackTimerRef.current = null;
      }
    };
  }, [isOpen, iframeSrc, revealIframe]);

  const closePopup = useCallback(
    (reason: VerifastPopupCloseReason) => {
      if (closeHandledRef.current) return;
      closeHandledRef.current = true;
      onClose(reason);
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    closeHandledRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !normalizedOrigin) return;

    const handler = (event: MessageEvent) => {
      if (event.origin.replace(/\/+$/, "") !== normalizedOrigin) {
        return;
      }

      try {
        const data = event.data as VerifastMessageData | undefined;
        const eventName =
          data && typeof data === "object" && typeof data.event === "string"
            ? data.event
            : "";

        if (eventName === "identity-verification-start") {
          revealIframe();
          onStarted?.();
          return;
        }

        if (STEP_COMPLETE_EVENTS.has(eventName)) {
          onStepComplete?.();
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to process identity verification response";
        setErrorMessage(message);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [isOpen, normalizedOrigin, onStarted, onStepComplete, revealIframe]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative flex max-h-[90vh] w-full max-w-[calc(100vw-24px)] flex-col rounded-lg bg-white shadow-xl sm:max-w-5xl">
        <button
          type="button"
          aria-label="Close"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            closePopup("manual");
          }}
          className="absolute right-4 top-4 z-[100] flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center justify-center border-b border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Verify Your Identity
          </h2>
        </div>

        <div className="relative min-h-[760px] flex-1 overflow-hidden p-6">
          {iframeSrc ? (
            <iframe
              key={iframeSrc}
              src={iframeSrc}
              title="Verifast Identity Verification"
              allow="camera; microphone"
              className={
                iframeRevealed
                  ? "h-full min-h-[760px] w-full border-0"
                  : "pointer-events-none absolute left-0 top-0 h-[760px] w-full border-0 opacity-0"
              }
            />
          ) : null}

          {(isLoading || !iframeSrc || !iframeRevealed) && !errorMessage ? (
            <div className="absolute inset-0 z-20 flex min-h-[760px] items-center justify-center bg-white">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                <span className="text-gray-600">Loading identity verification...</span>
              </div>
            </div>
          ) : null}

          {errorMessage ? (
            <p className="mt-3 text-sm text-red-600">{errorMessage}</p>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
